/**
 * App-wide offline status indicator for the Layout header.
 * Hidden when online with no pending actions.
 * Shows red wifi-off icon when offline, amber badge when pending actions exist.
 */
import React from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { useOfflineStatus } from '../hooks/useOfflineStatus';

export const OfflineIndicator: React.FC = () => {
  const { t } = useLanguage();
  const { isOffline, pendingSyncCount } = useOfflineStatus();

  if (!isOffline && pendingSyncCount === 0) return null;

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium select-none"
      title={isOffline ? t('offline.youAreOffline') : `${pendingSyncCount} ${t('offline.pendingActions')}`}
    >
      {isOffline ? (
        <>
          <WifiOff className="h-4 w-4 text-red-500" />
          <span className="hidden sm:inline text-red-600 dark:text-red-400">{t('offline.offlineLabel')}</span>
        </>
      ) : (
        <>
          <RefreshCw className="h-4 w-4 text-amber-500 animate-spin" />
          <span className="text-amber-600 dark:text-amber-400">{pendingSyncCount}</span>
        </>
      )}
    </div>
  );
};
